'use client';

import { MoviesListSortOption, useMoviesListState } from '@/app/movies/components/movies-list-state-provider';
import { FiltersGrouped } from '@/components/dialogs/filter-dialog/filter-dialog';
import { useMovieCatalog } from '@/hooks/movies/movie-catalog';
import {
  MovieCatalogCategory,
  MovieCatalogFilters,
  MovieCatalogItem,
  MovieCatalogItemResponse,
  MovieCatalogLocation,
  MovieCatalogSorts,
} from '@/types/movie-catalog';
import { getRuntimeString } from '@/utils/movie-helper';
import { useMemo } from 'react';

type LegacyFilterOption = {
  displayName: string;
  value: string;
};

const legacyFormatOptions: LegacyFilterOption[] = [
  { displayName: 'Cinemark XD', value: 'XD' },
  { displayName: 'RealD 3D', value: '3D' },
  { displayName: 'IMAX', value: 'IMAX' },
  { displayName: 'D-BOX', value: 'D-BOX' },
  { displayName: 'Global', value: 'Global' },
  { displayName: 'Open Caption', value: 'Open Caption' },
  { displayName: 'Closed Caption', value: 'Closed Caption' },
];
const legacyLanguageOptions = [
  'Bengali',
  'Cantonese',
  'Chinese',
  'English',
  'Gujarati',
  'Hebrew',
  'Hindi',
  'Japanese',
  'Kannada',
  'Korean',
  'Mandarin',
  'Marathi',
  'Malayalam',
  'Pakistani',
  'Punjabi',
  'Spanish',
  'Tagalog',
  'Tamil',
  'Telugu',
  'Urdu',
];
const legacyRatingOptions = ['G', 'PG', 'PG-13', 'R'];

type UseMoviesListFiltersProps = {
  category: MovieCatalogCategory;
  location: MovieCatalogLocation;
  popularGenreNames: string[];
};

type UseMoviesListFiltersResult = {
  activeFilters: string[];
  filtersGrouped: FiltersGrouped;
  handleFilterChange: (next: FiltersGrouped) => void;
  handleSortChange: (next: MoviesListSortOption[]) => void;
  hasNoNearbyTheaters: boolean;
  isError: boolean;
  isLoading: boolean;
  movies: MovieCatalogItem[];
  removeFilter: (filter: string) => void;
  sortOptions: MoviesListSortOption[];
};

const filterOptionToItem = (
  type: string,
  option: LegacyFilterOption,
  filters: { [key: string]: boolean; }
): { displayName: string; id: string; selected: boolean; } => {
  const id = `${type}-${option.value}`;
  return { displayName: option.displayName, id, selected: !!filters[id] };
};

export const mapMovieToCatalogItem = (movie: MovieCatalogItemResponse): MovieCatalogItem => {
  const poster = movie.posterLargeImage || movie.posterMediumImage || movie.posterSmallImage;

  return {
    advanceTicketsEndDate: movie.advanceTicketsEndDate,
    advanceTicketsStartDate: movie.advanceTicketsStartDate,
    cinemarkMovieId: movie.cinemarkMovieId,
    genre: movie.genres.join(', '),
    movieId: movie.movieId,
    moviePosterAlt: movie.posterAltText || `${movie.movieTitle} Poster`,
    moviePosterUrl: poster?.url || undefined,
    movieRating: movie.rating,
    movieReleaseDate: movie.releaseDate,
    movieRuntime: getRuntimeString(movie.runningTime),
    movieTitle: movie.movieTitle,
    movieTrailerUrl: movie.trailerUrl || undefined,
    movieUrl: `/movies/${movie.slug}`,
    publicOnSaleDate: movie.publicOnSaleDate,
    theaterId: 0,
    theaterName: '',
  };
};

export const useMoviesListFilters = ({ category, location, popularGenreNames }: UseMoviesListFiltersProps): UseMoviesListFiltersResult => {
  const { filters, setFilters, setSortOptions, sortOptions } = useMoviesListState();
  const selectedSort = sortOptions.find(option => option.selected)?.id;
  const sort = selectedSort === 'release-date'
    ? MovieCatalogSorts.releaseDate
    : selectedSort === 'alphabetical'
      ? MovieCatalogSorts.alphabetical
      : MovieCatalogSorts.popular;
  const movieCatalogFilters = useMemo((): MovieCatalogFilters => ({
    formats: Object.keys(filters).filter(filter => filter.startsWith('format-') && filters[filter]).map(filter => filter.replace('format-', '')),
    genres: Object.keys(filters).filter(filter => filter.startsWith('genre-') && filters[filter]).map(filter => filter.replace('genre-', '')),
    languages: Object.keys(filters).filter(filter => filter.startsWith('language-') && filters[filter]).map(filter => filter.replace('language-', '')),
    ratings: Object.keys(filters).filter(filter => filter.startsWith('rating-') && filters[filter]).map(filter => filter.replace('rating-', '')),
    sort,
  }), [filters, sort]);
  const movieCatalogQuery = useMovieCatalog({
    businessDate: undefined,
    category,
    filters: movieCatalogFilters,
    location,
  });
  const hasLocation = !location.preserveCategoryMembership
    && (!!location.zipCode || location.latitude !== undefined && location.longitude !== undefined);
  const activeFilters = useMemo(() => {
    const selectedFilters = Object.entries(filters)
      .filter(([filter, selected]) => selected && /^(format|genre|language|rating)-/.test(filter))
      .map(([filter]) => filter);

    return selectedSort && selectedSort !== MovieCatalogSorts.popular
      ? [...selectedFilters, `sort-${selectedSort}`]
      : selectedFilters;
  }, [filters, selectedSort]);
  const filtersGrouped = useMemo((): FiltersGrouped => {
    const sortedPopularGenreNames = [...popularGenreNames].sort((firstGenre, secondGenre) => firstGenre.localeCompare(secondGenre));

    return {
      /*  eslint-disable @typescript-eslint/naming-convention */
      Genre: sortedPopularGenreNames.map(option => filterOptionToItem('genre', { displayName: option, value: option }, filters)),
      Language: legacyLanguageOptions.map(option => filterOptionToItem('language', { displayName: option, value: option }, filters)),
      Type: legacyFormatOptions.map(option => filterOptionToItem('format', option, filters)),
      /*  eslint-disable perfectionist/sort-objects */
      // key order is important to match figma and legacy, so we are disabling the sort-objects rule here
      Rating: legacyRatingOptions.map(option => filterOptionToItem('rating', { displayName: option, value: option }, filters)),
      /*  eslint-enable perfectionist/sort-objects */
      /*  eslint-enable @typescript-eslint/naming-convention */
    };
  }, [filters, popularGenreNames]);
  const movies = useMemo((): MovieCatalogItem[] => movieCatalogQuery.data?.movies.map(mapMovieToCatalogItem) || [], [movieCatalogQuery.data?.movies]);

  const handleFilterChange = (next: FiltersGrouped): void => {
    const nextFilters: { [key: string]: boolean; } = {};

    Object.values(next).forEach((group) => {
      group.forEach((filter) => {
        nextFilters[filter.id] = filter.selected;
      });
    });

    setFilters(nextFilters);
  };

  const handleSortChange = (next: MoviesListSortOption[]): void => {
    setSortOptions(next);
  };

  const removeFilter = (filter: string): void => {
    if (filter.startsWith('sort-')) {
      setSortOptions(previousOptions => previousOptions.map(option => ({
        ...option,
        selected: option.id === MovieCatalogSorts.popular,
      })));
      return;
    }

    setFilters(previousFilters => ({ ...previousFilters, [filter]: false }));
  };

  const hasNoNearbyTheaters = hasLocation
    ? !movieCatalogQuery.isLoading && !movieCatalogQuery.isError && !movieCatalogQuery.data?.location?.nearbyTheaters.length
    : false;

  return {
    activeFilters,
    filtersGrouped,
    handleFilterChange,
    handleSortChange,
    hasNoNearbyTheaters,
    isError: movieCatalogQuery.isError,
    isLoading: movieCatalogQuery.isLoading,
    movies,
    removeFilter,
    sortOptions,
  };
};
